import { whiteOrBlackTextColor } from 'helpers/StyleUtils';
import useWebsiteBuilder, { ImageContentStyle, WebsiteBuilderContent, WebsiteBuilderStyle } from 'Hooks/useWebsiteBuilder';
import Colors from 'modules/Colors';
import { styx } from 'modules/Utils';
import { CSSProperties } from 'react';

interface ImagePreviewContentProps {
    contentIndex: number;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
    style?: CSSProperties;
    onClickContentBlock?: (index: number) => void;
}

const ImagePreviewContent = ({
    contentIndex,
    websiteContent: websiteContentProp,
    websiteStyle: websiteStyleProp,
    style: styleProp,
    onClickContentBlock,
}: ImagePreviewContentProps) => {
    const {
        websiteContent: websiteContentState,
        websiteStyle: websiteStyleState,
    } = useWebsiteBuilder();

    const websiteContent = websiteContentProp || websiteContentState;
    const isContentBackgroundColorEnabled = websiteContent[contentIndex].isContentBackgroundColorEnabled;
    const contentBackgroundColor = websiteContent[contentIndex].contentBackgroundColor;

    const isContentTextColorEnabled = websiteContent[contentIndex].isContentTextColorEnabled;
    const contentTextColor = websiteContent[contentIndex].contentTextColor;

    const websiteStyle = websiteStyleProp || websiteStyleState;
    const {
        textColor: OLD,
        fontFamily,
        backgroundColor: backgroundColorWebsiteStyle,
    } = websiteStyle;

    const backgroundColor = isContentBackgroundColorEnabled ? contentBackgroundColor || backgroundColorWebsiteStyle || 'transparent' : backgroundColorWebsiteStyle || 'transparent';

    const imageContent = websiteContent[contentIndex].imageContent;

    const textColor = isContentTextColorEnabled ? contentTextColor : whiteOrBlackTextColor(backgroundColor);

    const {
        style: imageContentStyle,
    } = imageContent || {};

    const getImageContentStyle = () => {
        switch (imageContentStyle) {
            case ImageContentStyle.FULL_SCREEN:
                return {
                    outer: {
                        paddingLeft: 0,
                        paddingRight: 0,
                    },
                    inner: {
                        borderRadius: 0,
                        paddingBottom: '120%',
                    }
                };
            case ImageContentStyle.SQUARE:
                return {
                    outer: {},
                    inner: {
                        borderRadius: 0,
                    }
                };
            case ImageContentStyle.ROUNDED:
                return {
                    outer: {},
                    inner: {
                        borderRadius: 16,
                    }
                };
            case ImageContentStyle.CIRCLE:
                return {
                    outer: {},
                    inner: {
                        borderRadius: 1000,
                    }
                };
            case ImageContentStyle.SMALL_CIRCLE:
                return {
                    outer: {},
                    inner: {
                        borderRadius: 1000,
                        width: 100,
                        height: 100,
                        paddingBottom: 0,
                    }
                };
            default:
                return {
                    outer: {},
                    inner: {}
                };
        }
    }

    const renderImageSection = () => {
        return (
            <div
                className="imagePreviewContentTextContainer1"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {imageContent?.isTitle &&
                    <div
                        className="imagePreviewContentTitle"
                        style={{
                            marginTop: 'auto',
                            fontSize: 24,
                            fontWeight: 500,
                            color: textColor,
                            fontFamily: fontFamily,
                            textAlign: 'center',
                        }}
                    >
                        {imageContent?.title}
                    </div>
                }
                {imageContent?.isSubtitle &&
                    <div
                        className="imagePreviewContentSubtitle"
                        style={{
                            fontSize: 16,
                            color: textColor,
                            fontFamily: fontFamily,
                            textAlign: 'center',
                        }}
                    >
                        {imageContent?.subtitle}
                    </div>
                }
            </div>
        )
    }

    // NOTE: render()
    return (
        <div
            className="imagePreviewContentComponent"
            style={styx(
                sty.imagePreviewContentComponent,
                getImageContentStyle().outer,
                styleProp,
            )}
            onClick={() => onClickContentBlock?.(contentIndex)}
        >
            <div
                className="imagePreviewContent"
                style={styx(
                    sty.imagePreviewContent,
                    getImageContentStyle().inner,
                    {
                        backgroundImage: `url(${imageContent?.image || ''})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    },
                )}
            />
            {imageContent?.isTitle &&
                <div
                    className="imagePreviewContentTextContainer2"
                    style={styx(
                        sty.imagePreviewContentTextContainer,
                        imageContentStyle === ImageContentStyle.FULL_SCREEN ?
                            {
                                position: 'absolute',
                                minHeight: 100,
                                background: `linear-gradient(to bottom, transparent, ${backgroundColor})`,
                                left: 0,
                                right: 0,
                                bottom: 16,
                            }
                            :
                            {
                                marginTop: 8,
                            }
                    )}
                >
                    {renderImageSection()}
                </div>
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    imagePreviewContentComponent: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        paddingLeft: 16,
        paddingRight: 16,
    },
    imagePreviewContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'black',
        width: '100%',
        backgroundColor: Colors.grey,
        borderRadius: 16,
        paddingBottom: '100%'
    },
    imagePreviewContentTextContainer: {
        paddingLeft: 16,
        paddingRight: 16,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    }
}

export default ImagePreviewContent;